import type {
  WorksheetAssignSchedule,
  WorksheetAssignScheduleStatus,
} from '@mathflat/domain/Worksheet'
import clsx from 'clsx'
import { useState } from 'react'

import Button from '@/component/atoms/Button/Button/Button'
import { Icon } from '@/component/atoms/Icon'

import { SCHEDULE_LIST_STRINGS } from '../_constants/scheduleStrings'
import {
  cardStudents,
  formatScheduleDateTime,
  formatStatusLabel,
  formatStudentSummary,
  studentDisplayName,
  worksheetDisplayTitle,
} from '../_utils/scheduleListFormat'
import S from './ScheduleCard.style'

// 완료 카드는 지난 일이라 라벨만 남기고 나머지를 흐리게 그린다
const DONE_COLOR = 'var(--Colors-Gray-600)'

type Props = {
  detail: WorksheetAssignSchedule
  status: WorksheetAssignScheduleStatus
  /** 대기 탭에서만 준다. 없으면 취소 버튼을 그리지 않는다 */
  onCancel?: () => void
}

const ScheduleCard = ({ detail, status, onCancel }: Props) => {
  const names = cardStudents(detail, status).map(studentDisplayName)
  // 카드를 누르면 "첫 학생 외 n명" 요약이 전체 이름으로 펼쳐진다 (피그마 "카드 눌러서 학생 자세히 보기")
  const [expanded, setExpanded] = useState(false)
  const canExpand = names.length > 1
  return (
    <article
      css={[S.card, canExpand && S.expandable]}
      className={clsx(status === 'DONE' && 'done')}
      aria-label={worksheetDisplayTitle(detail)}
      aria-expanded={canExpand ? expanded : undefined}
      onClick={canExpand ? () => setExpanded((prev) => !prev) : undefined}
    >
      <div className="head">
        <div className="when">
          <Icon name="alarm_on" size={16} color={status === 'DONE' ? DONE_COLOR : undefined} />
          <span className="time">{formatScheduleDateTime(new Date(detail.scheduledAt))}</span>
          <span className={clsx('label', status === 'FAILED' && 'failed')}>
            ・{formatStatusLabel(detail, status)}
          </span>
        </div>
        {onCancel && (
          <Button
            size="xs"
            variant="outline"
            color="neutral"
            onClick={(e) => {
              e.stopPropagation()
              onCancel()
            }}
          >
            {SCHEDULE_LIST_STRINGS.cancel}
          </Button>
        )}
      </div>
      <p className="worksheet">{worksheetDisplayTitle(detail)}</p>
      <p className="students">{expanded ? names.join(', ') : formatStudentSummary(names)}</p>
    </article>
  )
}

export default ScheduleCard
